import React, { useState, useEffect, useMemo } from 'react';
import type { Campaign, Keyword, AudienceMetrics } from '@/types/campaign';
import type { CampaignOrganicMetrics } from '@/types/gsc';
import { LoadingSpinner } from '@/components/ui';
import { SearchTermsPanel } from './CampaignManagement/panels/SearchTermsPanel';
import {
  formatNumberCompact as formatNumber,
  formatCurrencyDisplay as formatCurrency,
  formatPercentDisplay as formatPercent,
} from './CampaignManagement/utils';

type MetricsTab = 'campaign' | 'ad-groups' | 'keywords' | 'search-terms' | 'audiences';

interface CampaignMetricsProps {
  campaign: Campaign;
  hasConversionTracking: boolean;
  isLoading?: boolean;
  organicMetrics?: CampaignOrganicMetrics | null;
}

const TAB_LABELS: Record<MetricsTab, string> = {
  'campaign': 'Campaign',
  'ad-groups': 'Ad Groups',
  'keywords': 'Keywords',
  'search-terms': 'Search Terms',
  'audiences': 'Audiences',
};

/**
 * Skeleton placeholder for loading state
 */
function MetricSkeleton() {
  return (
    <div className="h-5 w-12 bg-gray-200 rounded animate-pulse mx-auto" />
  );
}

/**
 * Tab bar for metrics card
 * Mobile: Segmented control (pill buttons)
 * Desktop: Underline tabs
 */
function MetricsTabBar({
  activeTab,
  onTabChange,
  availableTabs,
}: {
  activeTab: MetricsTab;
  onTabChange: (tab: MetricsTab) => void;
  availableTabs: MetricsTab[];
}) {
  return (
    <div className="mb-4">
      {/* Mobile: Segmented control */}
      <div className="sm:hidden">
        <div className="inline-flex rounded-lg bg-gray-100 p-1 w-full">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={`
                flex-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all
                ${activeTab === tab
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop: Underline tabs */}
      <div className="hidden sm:flex border-b border-gray-200">
        {availableTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => onTabChange(tab)}
            className={`
              px-4 py-2 text-sm font-medium transition-colors
              ${activeTab === tab
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }
            `}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Ad Groups metrics table
 */
function AdGroupsTab({ campaign }: { campaign: Campaign }) {
  const adGroups = campaign.ads_group_list || [];

  if (adGroups.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600">No ad group data available</p>
      </div>
    );
  }

  // Check if any ad group has metrics
  const hasMetrics = adGroups.some(ag =>
    ag.impressions !== undefined || ag.clicks !== undefined || ag.cost !== undefined
  );

  if (!hasMetrics) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600">No metrics available for ad groups</p>
        <p className="text-sm text-gray-500 mt-1">Metrics data is collected when your campaign runs</p>
      </div>
    );
  }

  // Add vertical scroll when more than 10 ad groups
  const needsScroll = adGroups.length > 10;

  return (
    <div className={`overflow-x-auto ${needsScroll ? 'max-h-[480px] overflow-y-auto' : ''}`}>
      <p className="text-xs text-gray-500 mb-2 text-right">28-Day Totals</p>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={`bg-gray-50 ${needsScroll ? 'sticky top-0 z-10' : ''}`}>
          <tr>
            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-400 w-8">
              #
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ad Group
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Impr
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Clicks
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cost
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Conv
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              CTR
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Avg CPC
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {adGroups.map((adGroup, index) => (
            <tr key={adGroup.ad_group_id || adGroup.ads_group_id} className="hover:bg-gray-50">
              <td className="px-1.5 py-2 text-xs text-gray-400 w-8">
                {index + 1}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate" title={adGroup.ad_group_name || adGroup.ads_group_name}>
                {adGroup.ad_group_name || adGroup.ads_group_name}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(adGroup.impressions)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(adGroup.clicks)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatCurrency(adGroup.cost)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(adGroup.conversions)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatPercent(adGroup.ctr)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatCurrency(adGroup.avg_cpc)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Keywords metrics table (aggregated across all ad groups)
 */
function KeywordsTab({ campaign, hasGscData }: { campaign: Campaign; hasGscData: boolean }) {
  const [organicData, setOrganicData] = useState<Map<string, { clicks: number; impressions: number }>>(new Map());
  const [isLoadingOrganic, setIsLoadingOrganic] = useState(false);

  // Fetch organic keyword data when GSC is available
  // Re-fetches when campaign changes to get organic data for the new campaign
  useEffect(() => {
    // Clear stale data when campaign changes
    setOrganicData(new Map());

    if (!hasGscData) return;

    // Track if this effect has been cancelled (user switched campaigns)
    let cancelled = false;

    const fetchOrganicData = async () => {
      setIsLoadingOrganic(true);
      try {
        const { getKeywordMetrics } = await import('@/services/api/gsc');
        const data = await getKeywordMetrics({ campaign_id: campaign.campaign_id, days: 28 });

        // Don't update state if campaign changed while request was in flight
        if (cancelled) return;

        // Build a map of keyword_id -> { clicks, impressions }
        const organicMap = new Map<string, { clicks: number; impressions: number }>();
        for (const kw of data) {
          organicMap.set(kw.keyword_id, {
            clicks: kw.organic_clicks,
            impressions: kw.organic_impressions,
          });
        }
        setOrganicData(organicMap);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch organic keyword data:', err);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingOrganic(false);
        }
      }
    };

    fetchOrganicData();

    // Cleanup: mark as cancelled when effect re-runs or component unmounts
    return () => {
      cancelled = true;
    };
  }, [hasGscData, campaign.campaign_id]);

  // Collect keywords from all ad groups
  const adGroups = campaign.ads_group_list || [];
  const allKeywords: Array<Keyword & { adGroupName: string }> = [];

  for (const adGroup of adGroups) {
    const keywords = adGroup.keywords_list || [];
    for (const kw of keywords) {
      allKeywords.push({
        ...kw,
        adGroupName: adGroup.ad_group_name || adGroup.ads_group_name || 'Unknown',
      });
    }
  }

  if (allKeywords.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600">No keyword data available</p>
      </div>
    );
  }

  // Check if any keyword has metrics
  const hasMetrics = allKeywords.some(kw =>
    kw.impressions !== undefined || kw.clicks !== undefined || kw.cost !== undefined
  );

  if (!hasMetrics) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600">No metrics available for keywords</p>
        <p className="text-sm text-gray-500 mt-1">Metrics data is collected when your keywords receive traffic</p>
      </div>
    );
  }

  // Sort by cost descending (most spend first) and limit to 100
  const sortedKeywords = [...allKeywords]
    .sort((a, b) => (b.cost || 0) - (a.cost || 0))
    .slice(0, 100);

  // Show "Top 100" indicator when we have more than 100 keywords
  const showTop100Indicator = allKeywords.length > 100;

  // Show organic column when GSC is available (we'll fetch the data)
  const showOrganicColumn = hasGscData;

  // Add vertical scroll when more than 10 keywords
  const needsScroll = sortedKeywords.length > 10;

  return (
    <div className={`overflow-x-auto ${needsScroll ? 'max-h-[480px] overflow-y-auto' : ''}`}>
      <div className="flex justify-between items-center mb-2">
        {showTop100Indicator ? (
          <p className="text-xs text-gray-500">Top 100 Keywords by Cost</p>
        ) : (
          <span></span>
        )}
        <p className="text-xs text-gray-500">28-Day Totals</p>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={`bg-gray-50 ${needsScroll ? 'sticky top-0 z-10' : ''}`}>
          <tr>
            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-400 w-8">
              #
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Keyword
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Match
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ad Group
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Impr
            </th>
            {showOrganicColumn && (
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Org Impr
              </th>
            )}
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Clicks
            </th>
            {showOrganicColumn && (
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                Org Clicks
              </th>
            )}
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cost
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Conv
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedKeywords.map((keyword, index) => (
            <tr key={`${keyword.keyword_id}-${index}`} className="hover:bg-gray-50">
              <td className="px-1.5 py-2 text-xs text-gray-400 w-8">
                {index + 1}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 max-w-xs truncate" title={keyword.keyword_text}>
                {keyword.keyword_text}
              </td>
              <td className="px-3 py-2 text-sm">
                <span className="px-2 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-gray-700 whitespace-nowrap">
                  {keyword.match_type}
                </span>
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 max-w-xs truncate" title={keyword.adGroupName}>
                {keyword.adGroupName}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(keyword.impressions)}
              </td>
              {showOrganicColumn && (
                <td className="px-3 py-2 text-sm text-gray-500 text-right">
                  {isLoadingOrganic ? (
                    <span className="text-gray-400">...</span>
                  ) : (
                    formatNumber(organicData.get(keyword.keyword_id)?.impressions)
                  )}
                </td>
              )}
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(keyword.clicks)}
              </td>
              {showOrganicColumn && (
                <td className="px-3 py-2 text-sm text-gray-500 text-right">
                  {isLoadingOrganic ? (
                    <span className="text-gray-400">...</span>
                  ) : (
                    formatNumber(organicData.get(keyword.keyword_id)?.clicks)
                  )}
                </td>
              )}
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatCurrency(keyword.cost)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 text-right">
                {formatNumber(keyword.conversions)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Audiences metrics table (for Demand Gen campaigns)
 * Shows performance metrics for each audience segment
 */
function AudiencesTab({ campaign }: { campaign: Campaign }) {
  const [audienceMetrics, setAudienceMetrics] = useState<AudienceMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch audience metrics when campaign changes
  useEffect(() => {
    let cancelled = false;

    const fetchAudienceMetrics = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { campaignsApi } = await import('@/services/api/campaigns');
        const data = await campaignsApi.getAudienceMetrics(campaign.campaign_id);

        if (cancelled) return;

        setAudienceMetrics(data.audiences || []);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch audience metrics:', err);
          setError('Failed to load audience metrics');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAudienceMetrics();

    return () => {
      cancelled = true;
    };
  }, [campaign.campaign_id]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-red-50 rounded-lg border border-red-200">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (audienceMetrics.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600">No audience metrics available</p>
        <p className="text-sm text-gray-500 mt-1">Metrics appear when your audiences receive traffic</p>
      </div>
    );
  }

  // Show "Top 100" indicator when we have more than 100 audiences
  const showTop100Indicator = audienceMetrics.length > 100;
  const displayMetrics = audienceMetrics.slice(0, 100);

  // Add vertical scroll when more than 10 rows
  const needsScroll = displayMetrics.length > 10;

  // Helper to format audience type
  const formatAudienceType = (type: string): string => {
    const typeMap: Record<string, string> = {
      'USER_LIST': 'User List',
      'AUDIENCE': 'Audience',
      'CUSTOM_AUDIENCE': 'Custom',
      'COMBINED_AUDIENCE': 'Combined',
    };
    return typeMap[type] || type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  // Helper to get badge color for audience type
  const getTypeColor = (type: string): string => {
    const colorMap: Record<string, string> = {
      'USER_LIST': 'bg-blue-100 text-blue-800',
      'AUDIENCE': 'bg-purple-100 text-purple-800',
      'CUSTOM_AUDIENCE': 'bg-indigo-100 text-indigo-800',
      'COMBINED_AUDIENCE': 'bg-violet-100 text-violet-800',
    };
    return colorMap[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className={`overflow-x-auto ${needsScroll ? 'max-h-[480px] overflow-y-auto' : ''}`}>
      <div className="flex justify-between items-center mb-2">
        {showTop100Indicator ? (
          <p className="text-xs text-gray-500">Top 100 Audiences by Cost</p>
        ) : (
          <span></span>
        )}
        <p className="text-xs text-gray-500">30-Day Totals</p>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className={`bg-gray-50 ${needsScroll ? 'sticky top-0 z-10' : ''}`}>
          <tr>
            <th className="px-1.5 py-2 text-left text-xs font-medium text-gray-400 w-8">
              #
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Audience
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ad Group
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Impr
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Clicks
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Cost
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Conv
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              CTR
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {displayMetrics.map((audience, index) => (
            <tr key={`${audience.criterion_id}-${audience.ad_group_id}`} className="hover:bg-gray-50">
              <td className="px-1.5 py-2 text-xs text-gray-400">
                {index + 1}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 max-w-[200px] truncate" title={audience.audience_name}>
                {audience.audience_name}
              </td>
              <td className="px-3 py-2 text-sm">
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(audience.audience_type)}`}>
                  {formatAudienceType(audience.audience_type)}
                </span>
              </td>
              <td className="px-3 py-2 text-sm text-gray-600 max-w-[150px] truncate" title={audience.ad_group_name}>
                {audience.ad_group_name}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                {formatNumber(audience.impressions)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                {formatNumber(audience.clicks)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                {formatCurrency(audience.cost)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                {audience.conversions.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-sm text-gray-900 text-right">
                {audience.ctr.toFixed(2)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Campaign-level metrics (original content)
 */
function CampaignTab({
  campaign,
  hasConversionTracking,
  isLoading,
  organicMetrics,
}: {
  campaign: Campaign;
  hasConversionTracking: boolean;
  isLoading: boolean;
  organicMetrics?: CampaignOrganicMetrics | null;
}) {
  const showSkeleton = isLoading;

  // Standard layout without organic data
  if (!organicMetrics?.has_gsc_data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 text-center border-b pb-2">
          <div className="text-xs font-medium text-gray-500">Metric</div>
          <div className="hidden lg:block"></div>
          <div className="text-xs font-medium text-gray-500 w-20 whitespace-nowrap">7-Day Avg</div>
          <div className="text-xs font-medium text-gray-500 w-20 whitespace-nowrap">28-Day Avg</div>
        </div>
        <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 items-center">
          <div className="font-medium text-gray-700">Clicks:</div>
          <div className="hidden lg:block"></div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : ((campaign.clicks_7d ?? 0) / 7).toFixed(1)}
          </div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : (campaign.clicks / 28).toFixed(1)}
          </div>
        </div>
        <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 items-center">
          <div className="font-medium text-gray-700">Impressions:</div>
          <div className="hidden lg:block"></div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : ((campaign.impressions_7d ?? 0) / 7).toFixed(1)}
          </div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : (campaign.impressions / 28).toFixed(1)}
          </div>
        </div>
        <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 items-center">
          <div className="font-medium text-gray-700">Cost:</div>
          <div className="hidden lg:block"></div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : `$${((campaign.cost_7d ?? 0) / 7).toFixed(2)}`}
          </div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : `$${(campaign.cost / 28).toFixed(2)}`}
          </div>
        </div>
        {hasConversionTracking && (
          <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 items-center">
            <div className="font-medium text-gray-700">Conversions:</div>
            <div className="hidden lg:block"></div>
            <div className="text-gray-900 text-center w-20">
              {showSkeleton ? <MetricSkeleton /> : ((campaign.conversions_7d ?? 0) / 7).toFixed(2)}
            </div>
            <div className="text-gray-900 text-center w-20">
              {showSkeleton ? <MetricSkeleton /> : (campaign.conversions / 28).toFixed(2)}
            </div>
          </div>
        )}
        <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 items-center">
          <div className="font-medium text-gray-700">CTR:</div>
          <div className="hidden lg:block"></div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : `${(campaign.ctr_7d ?? 0).toFixed(1)}%`}
          </div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : `${campaign.ctr.toFixed(1)}%`}
          </div>
        </div>
        <div className="grid grid-cols-[5rem_auto_auto] lg:grid-cols-[7rem_10rem_auto_auto] gap-4 items-center">
          <div className="font-medium text-gray-700">CPC:</div>
          <div className="hidden lg:block"></div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : `$${(campaign.cpc_7d ?? 0).toFixed(2)}`}
          </div>
          <div className="text-gray-900 text-center w-20">
            {showSkeleton ? <MetricSkeleton /> : `$${campaign.cpc.toFixed(2)}`}
          </div>
        </div>
      </div>
    );
  }

  // Enhanced layout with Paid + Organic columns
  const organic = organicMetrics;

  return (
    <div className="space-y-3">
      {/* Header row with sub-columns */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 text-center border-b pb-2">
        <div className="text-xs font-medium text-gray-500"></div>
        <div className="col-span-2 text-xs font-medium text-gray-500 pb-1 mx-2 sm:mx-8 border-b border-gray-300 whitespace-nowrap">7-Day Avg</div>
        <div className="col-span-2 text-xs font-medium text-gray-500 pb-1 mx-2 sm:mx-8 border-b border-gray-300 whitespace-nowrap">28-Day Avg</div>
      </div>
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 text-center border-b pb-2">
        <div className="text-xs font-medium text-gray-500">Metric</div>
        <div className="text-xs font-medium text-gray-700">Paid</div>
        <div className="text-xs font-medium text-gray-500">Organic</div>
        <div className="text-xs font-medium text-gray-700">Paid</div>
        <div className="text-xs font-medium text-gray-500">Organic</div>
      </div>

      {/* Clicks row */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
        <div className="font-medium text-gray-700">Clicks:</div>
        <div className="text-gray-900 text-center">
          {isLoading ? <MetricSkeleton /> : ((campaign.clicks_7d ?? 0) / 7).toFixed(1)}
        </div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : (organic.organic_clicks_7d / 7).toFixed(1)}
        </div>
        <div className="text-gray-900 text-center">
          {isLoading ? <MetricSkeleton /> : (campaign.clicks / 28).toFixed(1)}
        </div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : (organic.organic_clicks_28d / 28).toFixed(1)}
        </div>
      </div>

      {/* Impressions row */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
        <div className="font-medium text-gray-700">Impressions:</div>
        <div className="text-gray-900 text-center">
          {isLoading ? <MetricSkeleton /> : formatNumber((campaign.impressions_7d ?? 0) / 7)}
        </div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : formatNumber(organic.organic_impressions_7d / 7)}
        </div>
        <div className="text-gray-900 text-center">
          {isLoading ? <MetricSkeleton /> : formatNumber(campaign.impressions / 28)}
        </div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : formatNumber(organic.organic_impressions_28d / 28)}
        </div>
      </div>

      {/* CTR row */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
        <div className="font-medium text-gray-700">CTR:</div>
        <div className="text-gray-900 text-center">
          {isLoading ? <MetricSkeleton /> : `${(campaign.ctr_7d ?? 0).toFixed(1)}%`}
        </div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : `${organic.organic_ctr_7d.toFixed(1)}%`}
        </div>
        <div className="text-gray-900 text-center">
          {isLoading ? <MetricSkeleton /> : `${campaign.ctr.toFixed(1)}%`}
        </div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : `${organic.organic_ctr_28d.toFixed(1)}%`}
        </div>
      </div>

      {/* Position row (organic only) */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
        <div className="font-medium text-gray-700">Position:</div>
        <div className="text-gray-400 text-center">—</div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : (
            organic.avg_position_7d !== null
              ? organic.avg_position_7d.toFixed(1)
              : '—'
          )}
        </div>
        <div className="text-gray-400 text-center">—</div>
        <div className="text-gray-500 text-center">
          {isLoading ? <MetricSkeleton /> : (
            organic.avg_position_28d !== null
              ? organic.avg_position_28d.toFixed(1)
              : '—'
          )}
        </div>
      </div>

      {/* Divider for paid-only metrics */}
      <div className="border-t pt-3 mt-2">
        <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 text-center pb-2">
          <div className="text-xs font-medium text-gray-500">Metric</div>
          <div className="col-span-2 text-xs font-medium text-gray-500 whitespace-nowrap">7-Day Avg</div>
          <div className="col-span-2 text-xs font-medium text-gray-500 whitespace-nowrap">28-Day Avg</div>
        </div>
      </div>

      {/* Cost row (paid only) */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
        <div className="font-medium text-gray-700">Cost:</div>
        <div className="text-gray-900 text-center col-span-2">
          {isLoading ? <MetricSkeleton /> : `$${((campaign.cost_7d ?? 0) / 7).toFixed(2)}`}
        </div>
        <div className="text-gray-900 text-center col-span-2">
          {isLoading ? <MetricSkeleton /> : `$${(campaign.cost / 28).toFixed(2)}`}
        </div>
      </div>

      {/* CPC row (paid only) */}
      <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
        <div className="font-medium text-gray-700">CPC:</div>
        <div className="text-gray-900 text-center col-span-2">
          {isLoading ? <MetricSkeleton /> : `$${(campaign.cpc_7d ?? 0).toFixed(2)}`}
        </div>
        <div className="text-gray-900 text-center col-span-2">
          {isLoading ? <MetricSkeleton /> : `$${campaign.cpc.toFixed(2)}`}
        </div>
      </div>

      {/* Conversions row (paid only) */}
      {hasConversionTracking && (
        <div className="grid grid-cols-[5rem_1fr_1fr_1fr_1fr] sm:grid-cols-[5.5rem_1fr_1fr_1fr_1fr] gap-1 sm:gap-2 items-center text-sm">
          <div className="font-medium text-gray-700">Conversions:</div>
          <div className="text-gray-900 text-center col-span-2">
            {isLoading ? <MetricSkeleton /> : ((campaign.conversions_7d ?? 0) / 7).toFixed(2)}
          </div>
          <div className="text-gray-900 text-center col-span-2">
            {isLoading ? <MetricSkeleton /> : (campaign.conversions / 28).toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Performance metrics card with tabs for Campaign, Ad Groups, Keywords, and Search Terms.
 * Shows Paid + Organic columns when GSC data is available.
 */
export function CampaignMetrics({
  campaign,
  hasConversionTracking,
  isLoading = false,
  organicMetrics,
}: CampaignMetricsProps) {
  const [activeTab, setActiveTab] = useState<MetricsTab>('campaign');

  // Determine available tabs based on campaign type
  const campaignType = campaign.campaign_type?.toUpperCase() || '';
  const isSearchCampaign = campaignType === 'SEARCH' || campaignType.includes('AI_MAX');
  const isDemandGen = campaignType.includes('DEMAND_GEN');
  const hasAdGroups = (campaign.ads_group_list?.length || 0) > 0;

  // Build available tabs - memoize to avoid recreating on every render
  const availableTabs = useMemo(() => {
    const tabs: MetricsTab[] = ['campaign'];
    if (hasAdGroups) {
      tabs.push('ad-groups');
      // Keywords only for Search/AI Max campaigns, not Demand Gen (which uses audience targeting)
      if (isSearchCampaign && !isDemandGen) {
        tabs.push('keywords');
      }
      // Audiences tab for Demand Gen campaigns (audience-based targeting)
      if (isDemandGen) {
        tabs.push('audiences');
      }
    }
    if (isSearchCampaign && !isDemandGen) {
      tabs.push('search-terms');
    }
    return tabs;
  }, [hasAdGroups, isSearchCampaign, isDemandGen]);

  // Reset activeTab when campaign changes or when current tab is no longer available
  // This prevents showing stale tab content (e.g., search-terms for a non-Search campaign)
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab('campaign');
    }
  }, [campaign.campaign_id, availableTabs, activeTab]);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'campaign':
        return (
          <CampaignTab
            campaign={campaign}
            hasConversionTracking={hasConversionTracking}
            isLoading={isLoading}
            organicMetrics={organicMetrics}
          />
        );
      case 'ad-groups':
        return <AdGroupsTab campaign={campaign} />;
      case 'keywords':
        return <KeywordsTab campaign={campaign} hasGscData={organicMetrics?.has_gsc_data || false} />;
      case 'search-terms':
        return <SearchTermsPanel campaign={campaign} hasGscData={organicMetrics?.has_gsc_data || false} />;
      case 'audiences':
        return <AudiencesTab campaign={campaign} />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow border">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Performance Metrics</h2>
      {availableTabs.length > 1 && (
        <MetricsTabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          availableTabs={availableTabs}
        />
      )}
      {renderTabContent()}
    </div>
  );
}
